import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { RouteTransition } from "./components/RouteTransition";

// Landing page loaded eagerly for fast initial paint
import Landing from "./pages/Landing";

// Lazy load all other pages for code splitting
const MyOrders = lazy(() => import("./pages/MyOrders"));
const Register = lazy(() => import("./pages/Register"));
const Login = lazy(() => import("./pages/Login"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const SelectRelationship = lazy(() => import("./pages/SelectRelationship"));
const Create = lazy(() => import("./pages/Create"));
const Payment = lazy(() => import("./pages/Payment"));
const FramePayment = lazy(() => import("./pages/FramePayment"));
const PaymentVerify = lazy(() => import("./pages/PaymentVerify"));
const QRView = lazy(() => import("./pages/QRView"));
const MyQRCodes = lazy(() => import("./pages/MyQRCodes"));
const EditQR = lazy(() => import("./pages/EditQR"));
const Demo = lazy(() => import("./pages/Demo"));
const DemoPreview = lazy(() => import("./pages/DemoPreview"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Profile = lazy(() => import("./pages/Profile"));
const Activity = lazy(() => import("./pages/Activity"));
const DeliveryAddresses = lazy(() => import("./pages/DeliveryAddresses"));
const AdminVerification = lazy(() => import("./pages/AdminVerification"));
const EstablishmentsLanding = lazy(() => import("./pages/EstablishmentsLanding"));
const EstablishmentAuth = lazy(() => import("./pages/EstablishmentAuth"));
const EstablishmentDashboard = lazy(() => import("./pages/EstablishmentDashboard"));
const EstablishmentProfile = lazy(() => import("./pages/EstablishmentProfile"));
const EstablishmentStatistics = lazy(() => import("./pages/EstablishmentStatistics"));
const EstablishmentSettings = lazy(() => import("./pages/EstablishmentSettings"));
const CreateFrame = lazy(() => import('./pages/CreateFrame'));
const CreateCollageType = lazy(() => import('./pages/CreateCollageType'));
const ManageCollageType = lazy(() => import('./pages/ManageCollageType'));
const FrameDetails = lazy(() => import('./pages/FrameDetails'));
const PaystackWebhookDocs = lazy(() => import('./pages/PaystackWebhookDocs').then(m => ({ default: m.PaystackWebhookDocs })));
const DeliverySettings = lazy(() => import('./pages/DeliverySettings').then(m => ({ default: m.DeliverySettings })));
const PaymentCallback = lazy(() => import('./pages/PaymentCallback').then(m => ({ default: m.PaymentCallback })));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentFailed = lazy(() => import('./pages/PaymentFailed'));
const DeliveryZones = lazy(() => import('./pages/DeliveryZones').then(m => ({ default: m.DeliveryZones })));
const Guides = lazy(() => import('./pages/Guides'));
const GuideDetail = lazy(() => import('./pages/GuideDetail'));
const FAQ = lazy(() => import('./pages/FAQ'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));

const queryClient = new QueryClient();

// Minimal loading fallback to avoid layout shift
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <main>
      <Suspense fallback={<PageLoader />}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<RouteTransition><Landing /></RouteTransition>} />
          <Route path="/auth" element={<RouteTransition><Auth /></RouteTransition>} />
          <Route path="/register" element={<RouteTransition><Register /></RouteTransition>} />
          <Route path="/login" element={<RouteTransition><Login /></RouteTransition>} />
          <Route path="/reset-password" element={<RouteTransition><ResetPassword /></RouteTransition>} />
          <Route path="/dashboard" element={<RouteTransition><Dashboard /></RouteTransition>} />
          <Route path="/admin" element={<RouteTransition><AdminDashboard /></RouteTransition>} />
          <Route path="/select-relationship" element={<RouteTransition><SelectRelationship /></RouteTransition>} />
          <Route path="/create" element={<RouteTransition><Create /></RouteTransition>} />
          <Route path="/payment/:id" element={<RouteTransition><Payment /></RouteTransition>} />
          <Route path="/frame-payment/:id" element={<RouteTransition><FramePayment /></RouteTransition>} />
          <Route path="/payment/verify" element={<RouteTransition><PaymentVerify /></RouteTransition>} />
          <Route path="/qr/:id" element={<RouteTransition><QRView /></RouteTransition>} />
          <Route path="/my-qr-codes" element={<RouteTransition><MyQRCodes /></RouteTransition>} />
          <Route path="/my-orders" element={<RouteTransition><MyOrders /></RouteTransition>} />
          <Route path="/edit/:id" element={<RouteTransition><EditQR /></RouteTransition>} />
          <Route path="/demo" element={<RouteTransition><Demo /></RouteTransition>} />
          <Route path="/demo-preview" element={<RouteTransition><DemoPreview /></RouteTransition>} />
          <Route path="/profile" element={<RouteTransition><Profile /></RouteTransition>} />
          <Route path="/activity" element={<RouteTransition><Activity /></RouteTransition>} />
          <Route path="/delivery-addresses" element={<RouteTransition><DeliveryAddresses /></RouteTransition>} />
          <Route path="/admin/verification" element={<RouteTransition><AdminVerification /></RouteTransition>} />
          <Route path="/establishments" element={<RouteTransition><EstablishmentsLanding /></RouteTransition>} />
          <Route path="/establishment/login" element={<RouteTransition><EstablishmentAuth /></RouteTransition>} />
          <Route path="/establishment/register" element={<RouteTransition><EstablishmentAuth /></RouteTransition>} />
          <Route path="/establishment/dashboard" element={<RouteTransition><EstablishmentDashboard /></RouteTransition>} />
          <Route path="/establishment/frames/create" element={<RouteTransition><CreateFrame /></RouteTransition>} />
          <Route path="/establishment/collages/create" element={<RouteTransition><CreateCollageType /></RouteTransition>} />
          <Route path="/establishment/collages/:id" element={<RouteTransition><ManageCollageType /></RouteTransition>} />
          <Route path="/establishment/frames/:id" element={<RouteTransition><FrameDetails /></RouteTransition>} />
          <Route path="/establishment/profile" element={<RouteTransition><EstablishmentProfile /></RouteTransition>} />
          <Route path="/establishment/statistics" element={<RouteTransition><EstablishmentStatistics /></RouteTransition>} />
          <Route path="/paystack/webhook-docs" element={<RouteTransition><PaystackWebhookDocs /></RouteTransition>} />
          <Route path="/establishment/delivery-settings" element={<RouteTransition><DeliverySettings /></RouteTransition>} />
          <Route path="/payment/callback" element={<RouteTransition><PaymentCallback /></RouteTransition>} />
          <Route path="/payment/success" element={<RouteTransition><PaymentSuccess /></RouteTransition>} />
          <Route path="/payment/failed" element={<RouteTransition><PaymentFailed /></RouteTransition>} />
          <Route path="/establishment/delivery-zones" element={<RouteTransition><DeliveryZones /></RouteTransition>} />
          <Route path="/establishment/settings" element={<RouteTransition><EstablishmentSettings /></RouteTransition>} />
          <Route path="/guides" element={<RouteTransition><Guides /></RouteTransition>} />
          <Route path="/guides/:guideId" element={<RouteTransition><GuideDetail /></RouteTransition>} />
          <Route path="/faq" element={<RouteTransition><FAQ /></RouteTransition>} />
          <Route path="/terms" element={<RouteTransition><TermsOfService /></RouteTransition>} />
          <Route path="/privacy" element={<RouteTransition><PrivacyPolicy /></RouteTransition>} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<RouteTransition><NotFound /></RouteTransition>} />
        </Routes>
      </Suspense>
    </main>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AnimatedRoutes />
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
